import { useState, useRef } from "react";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { uploadProfilePicture } from "@/utils/supabase";

interface ProfilePictureUploaderProps {
  userId: string;
  profilePicture: string;
  firstName?: string;
  lastName?: string;
  onPictureUpdate: (url: string) => void;
  disabled?: boolean;
}

const ProfilePictureUploader = ({
  userId,
  profilePicture,
  firstName,
  lastName,
  onPictureUpdate,
  disabled = false,
}: ProfilePictureUploaderProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    setIsUploading(true);

    try {
      const fullName = `${firstName || 'user'} ${lastName || ''}`.trim();
      const result = await uploadProfilePicture(userId, file, fullName);

      if (result.success && result.url) {
        setImageKey(Date.now());
        onPictureUpdate(result.url);
        
        toast({
          title: "Profile picture updated",
          description: "Your profile picture has been successfully updated.",
        });
      } else {
        toast({
          title: "Upload failed",
          description: `There was a problem uploading your profile picture: ${result.error?.message || "Unknown error"}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      toast({
        title: "Upload failed",
        description: "There was a problem uploading your profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageError = () => {
    console.error("Image failed to load:", profilePicture);
    if (profilePicture && profilePicture.includes('?')) {
      const baseUrl = profilePicture.split('?')[0];
      onPictureUpdate(`${baseUrl}?t=${Date.now()}`);
    }
  };

  return (
    <div className="relative">
      <Avatar className="h-32 w-32 sm:h-40 sm:w-40 border-4 border-white shadow-md">
        {profilePicture ? (
          <AvatarImage 
            src={`${profilePicture}${profilePicture.includes('?') ? '&' : '?'}key=${imageKey}`}
            alt={`${firstName || 'User'} ${lastName || 'Profile'}`}
            onError={handleImageError}
          />
        ) : (
          <AvatarFallback className="text-4xl font-medium">
            {firstName?.charAt(0) || "U"}
          </AvatarFallback>
        )}
      </Avatar>
      
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleProfilePictureChange} 
        className="hidden" 
        accept="image/*"
      />
      
      {!disabled && (
        <Button 
          size="sm" 
          variant="outline"
          className="absolute bottom-2 right-2 rounded-full h-10 w-10 p-0 bg-white/90"
          onClick={triggerFileInput}
          disabled={isUploading}
        >
          {isUploading ? (
            <div className="h-4 w-4 border-2 border-emu-blue border-t-transparent rounded-full animate-spin" />
          ) : (
            <Camera size={16} />
          )}
        </Button>
      )}
    </div>
  );
};

export default ProfilePictureUploader;
